import React, { useEffect } from 'react';
import { PageHeader } from '@amzn/storm-ui-page-header';
import { connect } from 'react-redux';
import { RootState } from 'src/app/store';
import { Flag, selectIsMobileWeb, selectObjectInfo } from 'src/app/slice';
import didElementLoad from 'src/commons/utils/didElementLoad';
import { AttributeTypes } from './types';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';
import { selectRenderTagInPageName } from 'src/features/nav/store/slice';
import { formatFlagType } from 'src/commons/components/LaunchFlag/LaunchFlag.utils';
import { LaunchFlag } from 'src/commons/components/LaunchFlag/LaunchFlag';
import { Actions } from 'src/features/header/components/PageHeader/Actions/Actions';
import { Attributes } from 'src/features/header/components/PageHeader/Attributes/Attributes';
import { PageTitleIcon } from 'src/features/header/components/PageHeader/PageTitleIcon/PageTitleIcon';
import { PageTitleIconContextProvider } from 'src/features/header/components/PageHeader/PageTitleIcon/PageTitleIconContext';

export const ObjectInfo: React.VFC<{
  attributes: Array<AttributeTypes>;
  pageName?: string;
  objectType?: string;
  objectName?: string;
  attributeErrorMessage?: string;
  actionErrorMessage?: string;
  isMobileDevice: boolean;
  pageTitleTag?: Flag | null;
}> = ({
  attributes,
  objectType,
  objectName,
  attributeErrorMessage,
  actionErrorMessage,
  pageTitleTag,
}) => {
  const chromeConfig = useChromeConfigContext();
  const pageName = chromeConfig.pageHeader?.pageName;
  const actions = chromeConfig.pageHeader?.objectInfo?.actions?.list || [];
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const attributesList = Array.isArray(attributes)
    ? attributes
    : ((attributes as any).list ?? []);
  const hasAttributes = attributes && attributes.length > 0;
  const hasActions = actions && actions.length > 0;
  const hasPageTitle =
    (!!objectType && objectType.length > 0) ||
    (!!objectName && objectName.length > 0);
  const pageTitleIcon = chromeConfig.pageHeader?.pageTitleIcon;

  useEffect(() => {
    if (hasPageTitle || hasAttributes || hasActions) {
      didElementLoad('objectInfo');
    }
  }, []);

  if (!hasPageTitle && !hasAttributes && !hasActions) {
    return null;
  }

  const pageNameTag = pageTitleTag && (
    <LaunchFlag
      flag={{
        type: formatFlagType(pageTitleTag.type),
        label: pageTitleTag.label,
        description: pageTitleTag.description ?? pageTitleTag.label,
      }}
    />
  );

  return (
    <PageHeader
      prefix={objectType}
      title={objectName ?? (objectType && pageName)}
      renderAttributes={() => <Attributes attributes={attributesList} />}
      attributesErrorMessage={attributeErrorMessage}
      renderActions={() => <Actions actions={actions} />}
      actionsErrorMessage={actionErrorMessage}
      renderPageTitleIcon={() => (
        <PageTitleIconContextProvider>
          {pageNameTag}
          {pageTitleIcon && <PageTitleIcon {...pageTitleIcon} />}
        </PageTitleIconContextProvider>
      )}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  const objectInfo = selectObjectInfo(state);
  return {
    attributes: objectInfo?.attributes || [],
    objectType: objectInfo?.objectType,
    objectName: objectInfo?.objectName,
    attributeErrorMessage: objectInfo?.attributeErrorMessage,
    actionErrorMessage: objectInfo?.actionErrorMessage,
    isMobileDevice: selectIsMobileWeb(state),
    pageTitleTag: selectRenderTagInPageName(state),
  };
};

export const ConnectedObjectInfo = connect(mapStateToProps)(ObjectInfo);
